/**
 * valuePoints.ts — the "Value Points" library (GUI v2 feature 4; label per req A.2).
 *
 * Source of truth: 12_value-translation-library.md (+ 05_auto-tint-module.md,
 * 08_product-comparison-guide.md where cited). Copy is extracted, not invented;
 * spec numbers keep the "up to X% depending on shade" framing (26 §2). Cards
 * with status "pending" sit on FRAMEWORK SLOTS (30_tbd-register.md) and render
 * as visibly pending — never filled with invented content (req G.7).
 *
 * defaultRank is the no-signal order (req 4.4) — file 12's own presentation
 * order, PROPOSED (the guide states no explicit priority); flagged for Phil in
 * docs/LIVE_COACH_GUI2_2026-06-11.md. Ranking is re-weighted live by confirmed
 * reason chips (req 4.3 / A.8).
 */
import type { ReasonKey, ServiceInterest } from "@/types";

export type ValueProduct = "tint" | "ppf" | "coating";

export interface ValuePoint {
  id: string;
  headline: string;
  /** One line the advisor can say verbatim. */
  line: string;
  /** Expandable proof point / detail. */
  detail: string;
  products: ValueProduct[];
  /** Reason chips that boost this card (req 4.3). */
  reasons: ReasonKey[];
  defaultRank: number;
  status: "ok" | "pending";
  source: string;
}

export const VALUE_POINTS: ValuePoint[] = [
  {
    id: "ceramic-education-moment",
    headline: "Ceramic education moment",
    line: "Most people do not realize this, but the right film is as much about heat as looks.",
    detail:
      "Owner policy June 10, 2026: on every tint call, before the recommendation, deliver one or two enthusiastic sentences on ceramic heat rejection tied to the customer's life — our ceramic lines block up to 63 to 74 percent of total solar energy depending on line and shade. The recommendation then starts at Black Ceramic.",
    products: ["tint"],
    reasons: ["heat", "appearance", "privacy"],
    defaultRank: 1,
    status: "ok",
    source: "12_value-translation-library.md § How to Deliver Value Language",
  },
  {
    id: "heat-rejection",
    headline: "Heat rejection",
    line: "Black Ceramic rejects up to 63 percent of the sun's total energy depending on the shade you pick.",
    detail:
      "TSER by line: Black up to 43% depending on shade; Black Ceramic up to 63%; i3 up to 71%; i3+ 62–74%. Plain benefit: a noticeably cooler cabin, especially after the car sits in the sun. Wants-the-best line: \"i3+ is our maximum, 62 to 74 percent of total solar energy rejected depending on shade.\"",
    products: ["tint"],
    reasons: ["heat", "kids"],
    defaultRank: 2,
    status: "ok",
    source: "12_value-translation-library.md § 1. Heat Rejection",
  },
  {
    id: "ceramic-vs-dyed-construction",
    headline: "Ceramic vs dyed",
    line: "The dye layer gives you the look, the ceramic is what blocks the heat.",
    detail:
      "Infrared rejection: Black 13% IRR, Black Ceramic 60%, i3 93%, i3+ 99%. i3+ has four ceramic layers stacked in the film — that is how it gets to 99 percent. autobahn Black is the fallback option, not the opening recommendation: one clean step-down, then full respect for the decision.",
    products: ["tint"],
    reasons: ["heat", "comparing_quotes"],
    defaultRank: 3,
    status: "ok",
    source: "12_value-translation-library.md § 2. Ceramic Versus Dyed Construction",
  },
  {
    id: "uv-protection",
    headline: "UV protection",
    line: "Every film we carry blocks over 99 percent of UV.",
    detail:
      "Over 99% UV rejection on every autobahn line and shade — protects skin on every drive and slows interior fading and cracking. Premium-vehicle line: \"UV is what fades seats and cracks dashes. Blocking over 99 percent of it is one of the cheapest things you can do for that interior.\"",
    products: ["tint"],
    reasons: ["uv_protection", "kids", "interior_protection"],
    defaultRank: 4,
    status: "ok",
    source: "12_value-translation-library.md § 3. UV Protection",
  },
  {
    id: "glare-reduction",
    headline: "Glare reduction",
    line: "That low evening sun on the drive home stops being a squint contest.",
    detail:
      "Glare reduction is per shade, from the spec tables — quote the glare number for the actual shade being discussed, never a blanket number (examples: Black Ceramic 05 95%, i3 15 89%, i3 40 50%).",
    products: ["tint"],
    reasons: ["glare", "kids"],
    defaultRank: 5,
    status: "ok",
    source: "12_value-translation-library.md § 4. Glare Reduction",
  },
  {
    id: "privacy",
    headline: "Privacy",
    line: "The same shade that finishes the car keeps eyes out of it.",
    detail:
      "Darker VLT on side and rear glass, within Tint King official legal guidance. Commuter line: \"Your laptop bag stops being on display in the parking garage.\" Family line: \"Nobody window-shops the back seat, the kids' stuff, the car seats, any of it.\"",
    products: ["tint"],
    reasons: ["privacy", "appearance"],
    defaultRank: 6,
    status: "ok",
    source: "12_value-translation-library.md § 5. Privacy",
  },
  {
    id: "appearance",
    headline: "Appearance",
    line: "On a car like yours, the right shade looks factory, like it should have come that way.",
    detail:
      "A uniform, professionally matched shade across the glass — finished, intentional, more expensive-looking. Truck line: \"We match the front glass to your factory rear glass so the whole truck reads as one piece.\"",
    products: ["tint"],
    reasons: ["appearance", "privacy"],
    defaultRank: 7,
    status: "ok",
    source: "12_value-translation-library.md § 6. Appearance",
  },
  {
    id: "life-happens-warranty",
    headline: "Life Happens Warranty",
    line: "If life damages the film, it gets fixed without an argument about whose fault it was.",
    detail:
      "Tier coverage: Black and Black Ceramic include one-time no-fault coverage; i3 and i3+ include unlimited lifetime no-fault coverage. Exclusions: abuse, and major accidents involving an insurance claim. All claims go through Tint King. Comparing-quotes line: \"ask the other shop what happens when the film gets damaged and it is not a defect.\"",
    products: ["tint"],
    reasons: ["comparing_quotes"],
    defaultRank: 8,
    status: "ok",
    source: "12_value-translation-library.md § 7. Life Happens Warranty",
  },
  {
    id: "lifetime-manufacturer-warranty",
    headline: "Lifetime film warranty",
    line: "The film itself is guaranteed not to fail for as long as you own the car.",
    detail:
      "Lifetime manufacturer warranty on auto tint. Burned-before line: \"These films carry a lifetime manufacturer warranty, so that failure mode is off the table\" (purple fade, bubbling). One place to call, and it is us.",
    products: ["tint"],
    reasons: ["had_tint_before", "comparing_quotes"],
    defaultRank: 9,
    status: "ok",
    source: "12_value-translation-library.md § 8. Lifetime Manufacturer Warranty",
  },
  {
    id: "professional-installation",
    headline: "Install craftsmanship",
    line: "The difference between a good tint job and a bad one is edges and fit.",
    detail:
      "Trained installers, precision cutting and fitting — clean edges, no gaps, no peeling corners two summers from now. Burned-before: \"Everything you hated about the last job, the bubbles, the lifting edge, that is installation, not film.\"",
    products: ["tint"],
    reasons: ["had_tint_before", "comparing_quotes"],
    defaultRank: 10,
    status: "ok",
    source: "12_value-translation-library.md § 9. Professional Installation",
  },
  {
    id: "exclusive-autobahn-dealership",
    headline: "Exclusive autobahn installer",
    line: "We are the Central Indiana exclusive authorized installer for autobahn Performance Films.",
    detail:
      "This product, with these numbers and this warranty, is only available here in this market. \"Whoever else you call, they are quoting a different film. That is why two quotes can sound similar and be very different products.\"",
    products: ["tint"],
    reasons: ["comparing_quotes"],
    defaultRank: 11,
    status: "ok",
    source: "12_value-translation-library.md § 10. Exclusive autobahn Dealership",
  },
  {
    id: "showroom-heat-demo",
    headline: "Showroom heat demo",
    line: "We have a heat demo in the showroom where you put your hand behind each film.",
    detail:
      "Feel the difference between films before spending a dollar, plus an in-person 100% accurate quote. \"Thirty seconds and you will know exactly which one you want.\"",
    products: ["tint"],
    reasons: ["heat", "comparing_quotes"],
    defaultRank: 12,
    status: "ok",
    source: "12_value-translation-library.md § 11. The Showroom Heat Demo",
  },
  {
    id: "ac-fuel-supporting-benefit",
    headline: "A/C + fuel (supporting)",
    line: "Running the A/C less does save fuel, especially in summer — I can't give you an exact number.",
    detail:
      "Approved chain only: less heat enters the cabin → the A/C works less → an A/C that runs less uses less fuel. Lands hardest on i3 and i3+ (higher IRR = less A/C load); comfort stays the lead benefit. Never a specific MPG figure, dollar amount, or payback promise.",
    products: ["tint"],
    reasons: ["fuel_costs", "heat"],
    defaultRank: 13,
    status: "ok",
    source: "12_value-translation-library.md § 1 — A/C and fuel language rule",
  },
  {
    id: "quality-vs-cheaper-quote",
    headline: "Quote comparison",
    line: "Two quotes can sound similar and include very different film, coverage, and warranty.",
    detail:
      "Questions worth asking of any quote, including ours: which film line, by name? Which windows exactly? What warranty, in writing? No price matching — exclusive autobahn installer, and skilled labor is not comparable on price alone.",
    products: ["tint"],
    reasons: ["comparing_quotes"],
    defaultRank: 14,
    status: "ok",
    source: "08_product-comparison-guide.md § Honest Answers — cheaper quote",
  },
  {
    id: "local-presence-two-showrooms",
    headline: "Two local showrooms",
    line: "Which is closer for you, Indianapolis or Greenwood?",
    detail:
      "Indianapolis: 4358 Pendleton Pike, just west of the 465 and Pendleton Pike interchange. Greenwood: 494 N US 31, Suite B, two stoplights south of Greenwood Park Mall. Monday–Friday 9:00–5:30. Always a confirmed date and time, never \"stop by whenever.\"",
    products: ["tint"],
    reasons: ["comparing_quotes"],
    defaultRank: 15,
    status: "ok",
    source: "05_auto-tint-module.md § F. Close Hierarchy — showroom visit",
  },
  {
    id: "ppf-preserved-original-paint",
    headline: "Preserved original paint",
    line: "The paint under the film stays exactly the way it looks on delivery day.",
    detail:
      "Long-term owner line: \"Five years from now, the protected panels still look like year one. That is the product.\" Concept language only — package names, coverage, brand, and warranty are pending package definition.",
    products: ["ppf"],
    reasons: ["paint_protection", "new_vehicle", "resale_value"],
    defaultRank: 16,
    status: "ok",
    source: "12_value-translation-library.md § PPF — Preserved Original Paint",
  },
  {
    id: "ppf-highest-impact-area-coverage",
    headline: "Coverage where rocks land",
    line: "Almost everything the road throws at you hits the front of the car.",
    detail:
      "Protection lands where the rocks land. Budget-aware line: \"You do not have to wrap the whole car to solve the real problem.\" Coverage tiers pending package definition.",
    products: ["ppf"],
    reasons: ["paint_protection"],
    defaultRank: 17,
    status: "ok",
    source: "12_value-translation-library.md § PPF — Highest-Impact-Area Coverage",
  },
  {
    id: "ppf-new-vehicle-timing",
    headline: "New-vehicle timing",
    line: "The best day to protect the paint is before the first highway mile.",
    detail:
      "Protect perfect paint instead of chipped paint. \"You take delivery Friday. If we get you in right after, the film goes over flawless paint and stays that way.\"",
    products: ["ppf"],
    reasons: ["new_vehicle", "paint_protection"],
    defaultRank: 18,
    status: "ok",
    source: "12_value-translation-library.md § PPF — New-Vehicle Timing",
  },
  {
    id: "ppf-resale-support",
    headline: "Resale support",
    line: "A chipped-up nose is the first thing a buyer or a trade-in appraiser sees.",
    detail: "Protected panels with original, undamaged paint — a cleaner front end at sale or trade time.",
    products: ["ppf"],
    reasons: ["resale_value", "paint_protection"],
    defaultRank: 19,
    status: "ok",
    source: "12_value-translation-library.md § PPF — Resale Support",
  },
  {
    id: "ppf-packages-warranty",
    headline: "PPF packages + warranty",
    line: "Those details get confirmed at your consult.",
    detail:
      "PENDING package definition (30_tbd-register.md item 6) — no durability years or warranty terms by phone. Holding line: \"Those details get confirmed at your consult, and I'd rather give you the exact answer in writing than a guess on the phone.\"",
    products: ["ppf"],
    reasons: ["paint_protection"],
    defaultRank: 20,
    status: "pending",
    source: "12_value-translation-library.md § PPF (Framework) · 30_tbd-register.md #6",
  },
  {
    id: "coating-gloss",
    headline: "Gloss",
    line: "It looks freshly detailed on a random Tuesday, not just the day it was done.",
    detail:
      "A cured coating layer over the paint — a deep, wet-looking shine that lasts beyond any wax. \"That depth people describe as wet — that is what a coating does when the paint under it is corrected first.\"",
    products: ["coating"],
    reasons: ["appearance"],
    defaultRank: 21,
    status: "ok",
    source: "12_value-translation-library.md § COATING — Gloss",
  },
  {
    id: "coating-easier-washing",
    headline: "Easier washing",
    line: "Dirt does not stick the way it sticks to bare paint.",
    detail:
      "A slick, sealed surface dirt does not bond to — faster washes, less scrubbing. \"Bugs and road film wipe off instead of needing to be scraped off.\"",
    products: ["coating"],
    reasons: ["easier_maintenance"],
    defaultRank: 22,
    status: "ok",
    source: "12_value-translation-library.md § COATING — Easier Washing",
  },
  {
    id: "coating-correction-first",
    headline: "Correction-first quality",
    line: "A coating locks in whatever is under it.",
    detail:
      "\"If we coat over swirls, you have very shiny swirls forever. So we correct the paint first, then seal that finish in.\"",
    products: ["coating"],
    reasons: ["appearance"],
    defaultRank: 23,
    status: "ok",
    source: "12_value-translation-library.md § COATING — Correction-First Quality",
  },
  {
    id: "coating-realistic-maintenance",
    headline: "Honest maintenance",
    line: "A coating makes washing easier and faster — it does not end washing.",
    detail:
      "\"If anyone tells you that you will never wash the car again, they are selling you a disappointment.\" And the chip check: a coating does not stop rock chips — that is a film job.",
    products: ["coating"],
    reasons: ["easier_maintenance", "paint_protection"],
    defaultRank: 24,
    status: "ok",
    source: "12_value-translation-library.md § COATING — Realistic Maintenance Expectations",
  },
  {
    id: "coating-packages-warranty",
    headline: "Coating packages + warranty",
    line: "Those details get confirmed at your inspection.",
    detail:
      "PENDING package definition (30_tbd-register.md item 7) — no durability or warranty terms by phone; the close is the showroom inspection. Coating is never sold as rock chip protection.",
    products: ["coating"],
    reasons: ["easier_maintenance", "appearance"],
    defaultRank: 25,
    status: "pending",
    source: "12_value-translation-library.md § COATING (Framework) · 30_tbd-register.md #7",
  },
];

/** Which value products apply for a service interest (req G.6). */
export function productsForInterest(interest: ServiceInterest | null): ValueProduct[] {
  switch (interest) {
    case "ppf":
      return ["ppf"];
    case "ceramic_coating":
      return ["coating"];
    case "bundle":
      return ["tint", "ppf", "coating"];
    case "auto_tint":
      return ["tint"];
    default:
      return ["tint"];
  }
}

/**
 * Context-aware ranking (req 4.3): confirmed reason chips boost matching cards
 * (2 pts), unconfirmed chips boost less (1 pt); ties fall back to the guide's
 * default order. Pinned cards are handled by the panel, not here (req 4.5).
 */
export function rankValuePoints(
  points: ValuePoint[],
  reasons: { key: ReasonKey; confirmed: boolean }[],
): ValuePoint[] {
  const score = (p: ValuePoint): number =>
    reasons.reduce((sum, r) => (p.reasons.includes(r.key) ? sum + (r.confirmed ? 2 : 1) : sum), 0);
  return [...points].sort((a, b) => {
    const diff = score(b) - score(a);
    return diff !== 0 ? diff : a.defaultRank - b.defaultRank;
  });
}
